if (!customElements.get('p4m-mpm-make-browser')) {
  class P4MMpmMakeBrowser extends HTMLElement {
    static makeDefinitions = [
      { key: 'aprilia', label: 'Aprilia', patterns: [/\baprilia\b/i] },
      { key: 'benelli', label: 'Benelli', patterns: [/\bbenelli\b/i] },
      { key: 'bmw', label: 'BMW', patterns: [/\bbmw\b/i] },
      { key: 'brixton', label: 'Brixton', patterns: [/\bbrixton\b/i] },
      { key: 'buell', label: 'Buell', patterns: [/\bbuell\b/i] },
      { key: 'can-am', label: 'Can-Am', patterns: [/\bcan[- ]am\b/i] },
      { key: 'cfmoto', label: 'CFMOTO', patterns: [/\bcf\s?moto\b/i] },
      {
        key: 'ducati',
        label: 'Ducati',
        patterns: [/\bducati\b/i, /\bscrambler 10th anniversary rizoma edition\b/i]
      },
      {
        key: 'harley-davidson',
        label: 'Harley-Davidson',
        patterns: [/\bharley[- ]davidson\b/i, /\bharley\b/i]
      },
      { key: 'honda', label: 'Honda', patterns: [/\bhonda\b/i] },
      { key: 'husqvarna', label: 'Husqvarna', patterns: [/\bhusqvarna\b/i] },
      { key: 'indian', label: 'Indian', patterns: [/\bindian\b/i] },
      { key: 'jawa', label: 'Jawa', patterns: [/\bjawa\b/i] },
      { key: 'kawasaki', label: 'Kawasaki', patterns: [/\bkawasaki\b/i] },
      { key: 'ktm', label: 'KTM', patterns: [/\bktm\b/i] },
      { key: 'kymco', label: 'Kymco', patterns: [/\bkymco\b/i] },
      { key: 'moto-guzzi', label: 'Moto Guzzi', patterns: [/\bmoto guzzi\b/i] },
      { key: 'mv-agusta', label: 'MV Agusta', patterns: [/\bmv agusta\b/i] },
      { key: 'piaggio', label: 'Piaggio', patterns: [/\bpiaggio\b/i] },
      { key: 'royal-enfield', label: 'Royal Enfield', patterns: [/\broyal enfield\b/i] },
      { key: 'suzuki', label: 'Suzuki', patterns: [/\bsuzuki\b/i] },
      {
        key: 'triumph',
        label: 'Triumph',
        patterns: [
          /\btriumph\b/i,
          /\bbonneville\b/i,
          /\brocket 3(?: gt)?\b/i,
          /\bstreet triple 765 r\b/i,
          /\btiger 900\b/i
        ]
      },
      { key: 'vespa', label: 'Vespa', patterns: [/\bvespa\b/i] },
      { key: 'victory', label: 'Victory', patterns: [/\bvictory\b/i] },
      { key: 'yamaha', label: 'Yamaha', patterns: [/\byamaha\b/i] },
      { key: 'zero', label: 'Zero', patterns: [/\bzero\b/i] }
    ];

    connectedCallback() {
      if (this.dataset.p4mReady === 'true') return;
      this.dataset.p4mReady = 'true';
      this.abortController = new AbortController();
      this.collectionRoot = this.closest('results-list') || document;
      this.grid = this.querySelector('ul.product-grid');
      this.status = this.querySelector('[data-p4m-mpm-load-status]');
      this.navigation = this.querySelector('[data-p4m-mpm-make-navigation]');
      this.handleClick = (event) => {
        const link = event.target.closest('[data-p4m-mpm-make-link]');
        if (link && this.contains(link)) this.navigateToMake(event, link);
      };
      this.addEventListener('click', this.handleClick);

      if (this.grid && this.dataset.p4mMpmFragmentPath) {
        this.loadFullCollection();
      } else {
        this.showFallbackStatus();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.handleClick) this.removeEventListener('click', this.handleClick);
    }

    async loadFullCollection() {
      this.setAttribute('aria-busy', 'true');

      try {
        const firstFragment = await this.fetchFragment(1);
        const totalPages = Math.max(1, Number.parseInt(firstFragment.dataset.pages || '1', 10));
        const fragments = [firstFragment];

        if (totalPages > 1) {
          const requests = [];
          for (let page = 2; page <= totalPages; page += 1) {
            requests.push(this.fetchFragment(page));
          }
          fragments.push(...(await Promise.all(requests)));
        }

        this.renderFullCollection(fragments);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('P4M mat collection kept its paginated fallback.', error);
          this.showFallbackStatus();
        }
      } finally {
        this.setAttribute('aria-busy', 'false');
      }
    }

    async fetchFragment(page) {
      const url = this.fragmentUrl(page);
      let latestError;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortController.signal
          });
          if (!response.ok) throw new Error(`Mat catalog request failed with ${response.status}.`);

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fragment = documentFragment.querySelector('[data-p4m-mpm-all-fragment]');
          if (!fragment) throw new Error('The mat catalog response was incomplete.');
          return fragment;
        } catch (error) {
          latestError = error;
          if (error?.name === 'AbortError' || attempt === 1) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, 450));
        }
      }

      throw latestError;
    }

    fragmentUrl(page) {
      const url = new URL(this.dataset.p4mMpmFragmentPath, window.location.origin);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.forEach((value, key) => {
        if (key === 'sort_by' || key.startsWith('filter.')) url.searchParams.append(key, value);
      });
      url.searchParams.set('view', 'p4m-mpm-all');
      url.searchParams.set('page', String(page));
      return url.toString();
    }

    renderFullCollection(fragments) {
      const expectedTotal = Number.parseInt(fragments[0].dataset.total || '0', 10);
      const cardsByProduct = new Map();

      fragments.forEach((fragment) => {
        fragment.querySelectorAll('[data-p4m-mpm-fragment-card]').forEach((card) => {
          const productId = card.dataset.productId;
          if (productId && !cardsByProduct.has(productId)) cardsByProduct.set(productId, card);
        });
      });

      if (cardsByProduct.size === 0 || (expectedTotal > 0 && cardsByProduct.size !== expectedTotal)) {
        throw new Error(`Expected ${expectedTotal} mat products but received ${cardsByProduct.size}.`);
      }

      const groups = new Map();
      cardsByProduct.forEach((card) => {
        const make = this.classifyMake(card.dataset.p4mMpmTitle || '');
        if (!groups.has(make.key)) groups.set(make.key, { ...make, cards: [] });
        groups.get(make.key).cards.push(card);
      });

      const sortedGroups = Array.from(groups.values()).sort((left, right) => {
        if (left.key === 'universal-other') return 1;
        if (right.key === 'universal-other') return -1;
        return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
      });

      sortedGroups.forEach((group) => {
        group.cards.sort((left, right) =>
          (left.dataset.p4mMpmTitle || '').localeCompare(right.dataset.p4mMpmTitle || '', undefined, {
            numeric: true,
            sensitivity: 'base'
          })
        );
      });

      const content = document.createDocumentFragment();
      sortedGroups.forEach((group) => {
        content.append(this.makeHeading(group));
        group.cards.forEach((card) => content.append(card));
      });
      this.grid.replaceChildren(content);
      this.grid.dataset.lastPage = '1';
      this.grid.dataset.p4mMpmComplete = 'true';
      this.rebuildNavigation(sortedGroups);
      this.removePagination();

      if (this.status) {
        this.status.textContent = `All ${cardsByProduct.size} mats are organized by motorcycle brand below.`;
      }

      if (window.location.hash) this.activateHash(window.location.hash);
    }

    classifyMake(title) {
      const definition = P4MMpmMakeBrowser.makeDefinitions.find((make) =>
        make.patterns.some((pattern) => pattern.test(title))
      );
      return definition || { key: 'universal-other', label: 'Universal & Other' };
    }

    makeHeading(group) {
      const heading = document.createElement('li');
      heading.className = 'p4m-brand-section-heading p4m-mpm-make-section';
      heading.id = this.makeTargetId(group.key);
      heading.dataset.p4mMpmMakeSection = '';

      const eyebrow = document.createElement('span');
      eyebrow.className = 'p4m-brand-section-heading__eyebrow';
      eyebrow.textContent = 'Brand';

      const title = document.createElement('span');
      title.className = 'p4m-brand-section-heading__title';
      title.textContent = group.label;

      const count = document.createElement('span');
      count.className = 'p4m-brand-section-heading__count';
      count.textContent = `${group.cards.length} ${group.cards.length === 1 ? 'mat' : 'mats'}`;

      heading.append(eyebrow, title, count);
      return heading;
    }

    rebuildNavigation(groups) {
      if (!this.navigation) return;
      const links = document.createDocumentFragment();
      groups.forEach((group) => {
        const link = document.createElement('a');
        link.href = `#${this.makeTargetId(group.key)}`;
        link.dataset.p4mMpmMakeLink = '';

        const label = document.createElement('span');
        label.textContent = group.label;
        const count = document.createElement('b');
        count.textContent = String(group.cards.length);
        link.append(label, count);
        links.append(link);
      });
      this.navigation.replaceChildren(links);
    }

    makeTargetId(makeKey) {
      return `p4m-mpm-make-${makeKey}-${this.dataset.p4mMpmSectionId || 'collection'}`;
    }

    removePagination() {
      this.collectionRoot
        .querySelectorAll('.p4m-fitment-pagination, [ref="viewMoreNext"], [ref="viewMorePrevious"]')
        .forEach((element) => element.remove());
    }

    showFallbackStatus() {
      if (!this.status) return;
      const visibleProducts = this.grid ? this.grid.querySelectorAll('[data-product-id]').length : 0;
      this.status.textContent = `Showing ${visibleProducts || 'the available'} mats with standard pagination.`;
    }

    navigateToMake(event, link) {
      const targetId = link.hash ? decodeURIComponent(link.hash.slice(1)) : '';
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      event.preventDefault();
      this.setActiveLink(link);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const headerOffset = window.matchMedia('(max-width: 749px)').matches ? 88 : 108;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.history.replaceState(null, '', `#${targetId}`);
      window.scrollTo({ top: Math.max(0, targetTop), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    activateHash(hash) {
      const link = Array.from(this.querySelectorAll('[data-p4m-mpm-make-link]')).find(
        (candidate) => candidate.hash === hash
      );
      if (link) this.setActiveLink(link);
    }

    setActiveLink(activeLink) {
      this.querySelectorAll('[data-p4m-mpm-make-link]').forEach((link) => {
        const selected = link === activeLink;
        link.classList.toggle('is-active', selected);
        if (selected) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('p4m-mpm-make-browser', P4MMpmMakeBrowser);
}
